import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import { Loading } from "components/panels";
import React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { reorder } from "utilities/draggingutils";
import { type TableColumn, getColumnClassName, getColumnStyle } from "./common";
import { PhosphorCopySimpleButton, PhosphorPencilButton, PhosphorTrashButton } from "components/Atoms/PhosphorIconButton";

export interface OrderableTableFooter {
  key: string
  entries: React.ReactNode[]
  className?: string
}

export interface OrderableTableProps<T> {
  tableId?: string
  items: T[]
  isLoading: boolean
  getKey: (item: T) => string
  columns: Array<TableColumn<T>>
  footers?: OrderableTableFooter[]
  onOrderChanged: (newItems: T[]) => void
  disabled?: boolean
  onEdit?: (item: T) => void
  onDelete?: (item: T) => void
  onCopy?: (item: T) => void
}

const OrderableTable = <T extends any>(props: OrderableTableProps<T>) => {
  const {
    tableId = "orderable-table",
    items,
    isLoading,
    getKey,
    columns,
    footers = [],
    onOrderChanged,
    disabled,
    onEdit,
    onDelete,
    onCopy,
  } = props;

  const visibleColumns = columns.filter(column => !column.hidden);
  const visibleFooters = footers.map(footer => ({
    ...footer,
    entries: footer.entries.filter((_, index) => !columns[index].hidden)
  }));

  const onDragEnd = async (result) => {
    if (!result.destination) {
      return;
    }

    const oldItems = [...items];
    const newItems = reorder(oldItems, result.source.index, result.destination.index);

    onOrderChanged(newItems);
  };

  const displayEdit = !disabled && onEdit;
  const displayDelete = !disabled && onDelete;
  const displayCopy = !disabled && onCopy;

  return <>
    {isLoading && <Loading />}
    {!isLoading && items.length === 0 && <NoDataAvailableDiv />}
    {items.length > 0 && (
      <div className="list-with-gaps line-layout">
        <div className="list-with-gaps-header">
          <div>
            {!disabled && <div style={{ width: 16 }} />}
            {visibleColumns.map(column => (
              <div className={getColumnClassName(column)} style={getColumnStyle(column)} key={column.key}>{column.header}</div>
            ))}
            {displayCopy && <div style={{ width: 26 }} />}
            {displayDelete && <div style={{ width: 26 }} />}
            {displayEdit && <div style={{ width: 26 }} />}
          </div>
        </div>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={tableId}>
            {(provided) => (
              <div ref={provided.innerRef} className="list-with-gaps-body orderable-table">
                {items.map((item, index) => (
                  <Draggable
                    key={getKey(item)}
                    isDragDisabled={disabled}
                    draggableId={getKey(item)}
                    index={index}
                  >
                    {prov => (
                      <div ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}>
                        {!disabled && (<div>
                          <a className="icon-order" />
                        </div>)}
                        {visibleColumns.map(column => (
                          <div className={getColumnClassName(column, item)} style={getColumnStyle(column)} key={column.key}>{column.render(item)}</div>
                        ))}
                        {displayCopy && <PhosphorCopySimpleButton
                          title={RESX.Buttons.btnCopy}
                          layout="in-square"
                          onClick={() => onCopy(item)} />}

                        {displayDelete && <PhosphorTrashButton
                          title={RESX.Buttons.btnDelete}
                          layout="in-square"
                          onClick={() => onDelete(item)} />}

                        {displayEdit && <PhosphorPencilButton
                          title={RESX.Buttons.btnEdit}
                          layout="in-square"
                          onClick={() => onEdit(item)} />}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          {visibleFooters && <div className="list-with-gaps-footer">{
            visibleFooters.map((footer) => (
              <div className={`list-with-gaps-footer-row ${footer.className}`} key={footer.key}>
                {!disabled && <div style={{ width: 16 }} />}
                {footer.entries.map((entry, index) => (
                  <div className={getColumnClassName(columns[index])} style={getColumnStyle(columns[index])} key={columns[index].key}>{entry}</div>
                ))}
                {displayCopy && <div style={{ width: 26 }} />}
                {displayDelete && <div style={{ width: 26 }} />}
                {displayEdit && <div style={{ width: 26 }} />}
              </div>
            ))}</div>
          }
        </DragDropContext>
      </div>
    )}
  </>;
};

export default OrderableTable;